import {sleep} from '../../utils.ts';
import {getConfig} from '../lib/config.ts';
import {getApiCache} from './api-cache.ts';
import {ApiError} from './errors.ts';

type MakeApiCallParams = {
  method: 'GET' | 'POST';
  path: string;
  query?: URLSearchParams;
  headers?: Record<string, string>;
  body?: Blob | BufferSource | FormData | URLSearchParams | string;
  keepalive?: boolean;
};

export type RequestOptions = Partial<{
  abortSignal: AbortSignal;
  /** If true, save successful responses in a cache */
  useCache: boolean;
  /**
   * If true, continually retry until the server responds with something other than 202 or 503, or until timeout
   *
   * If the server responds with a Retry-After header, we'll respect that. Otherwise, we will retry using exponential
   * backoff + jitter to space our requests.
   * */
  poll: boolean;
  /** If polling, wait this long before retrying the first time (default: 1 second) */
  initialPollWaitMs: number;
  /** The maximum amount of jitter to add when retrying (the actual amount is randomly determined) (default: 1 second) */
  maxRetryJitterMs: number;
  /** The overall timeout for the API call, including retries (default: 2 minutes) */
  timeoutMs: number;
}>;

export type PostRequestOptions = Pick<RequestOptions, 'abortSignal' | 'timeoutMs'>;

export async function makeApiCall(params: MakeApiCallParams, opts: RequestOptions = {}): Promise<Response> {
  const options: Required<RequestOptions> = {
    abortSignal: undefined,
    useCache: false,
    poll: false,
    initialPollWaitMs: 1 * 1000, // 1 second
    maxRetryJitterMs: 1 * 1000, // 1 second
    timeoutMs: 2 * 60 * 1000, // 2 minutes
    ...opts,
  };

  // Never use the cache except for GET requests
  if (params.method !== 'GET') {
    options.useCache = false;
  }

  const config = getConfig();
  // always ensure there is a slash at the end of the base URL so that the path is added on after
  // c.f. https://developer.mozilla.org/en-US/docs/Web/API/URL_API/Resolving_relative_references
  const appUrl = config.appUrl + (config.appUrl.endsWith('/') ? '' : '/');
  const url = new URL(params.path, appUrl);

  params.query = params.query ?? new URLSearchParams();
  if (params.method === 'GET') {
    // Add the renderer version in the query string for cache-busting
    params.query.set('v', config.renderVersion);
  }
  url.search = params.query.toString();

  const combinedAbortSignal = AbortSignal.any([
    options.abortSignal,
    AbortSignal.timeout(options.timeoutMs),
  ].filter(Boolean));

  const req = new Request(url, {
    method: params.method,
    headers: params.headers,
    body: params.body ?? undefined,
    keepalive: params.keepalive ?? false,
    signal: combinedAbortSignal,
  });

  const cache = options.useCache ? await getApiCache() : null;
  const cachedResponse = await cache?.match(req);
  if (cachedResponse) {
    return cachedResponse;
  }

  const res = await poll(req, {
    ...options,
    abortSignal: combinedAbortSignal,
  });
  if (!res.ok) {
    throw new ApiError(`API error: received ${res.status} (${res.statusText})`, res);
  }

  if (req.method === 'GET' && res.status === 200) {
    await cache?.put(req, res.clone());
  }

  return res;
}

async function poll(req: Request, options: Required<RequestOptions>): Promise<Response> {
  let waitMs = options.initialPollWaitMs;
  while (true) {
    options.abortSignal.throwIfAborted();

    // TODO: relegate this no-restricted-syntax rule to Gitea code
    /* eslint-disable-next-line no-restricted-syntax -- we don't need CSRF, etc. for this */
    const res = await fetch(req.clone());

    // 202 and 503 (legacy) indicate that the data is still being processed
    // so we will need to poll for it (assuming we want to)
    if ((res.status === 202 || res.status === 503) && options.poll) {
      // Did we get a Retry-After header and is it a number?
      // (TODO: support dates)
      const retryAfter = Number.parseInt(res.headers.get('Retry-After'), 10);

      if (!Number.isNaN(retryAfter)) {
        await sleep(retryAfter * 1000); // Retry-After specifies a number of seconds
      } else {
        // No valid Retry-After header, use exponential backoff, plus some jitter
        await sleep(waitMs + (options.maxRetryJitterMs * Math.random()));
        // next try will wait twice as long
        waitMs *= 2;
      }
      continue;
    }

    return res;
  }
}
